import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngxs/store';
import { MenuItem } from 'primeng/api';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApplicationState } from '../../state/app.state';
import { DuesyState, DuesyStateService } from '../duesy-state.service';

/** Bottom-right launcher: the Duesy FAB, the ? help menu above it, and the panel. */
@Component({
  selector: 'app-duesy-widget',
  templateUrl: './duesy-widget.component.html',
  styleUrls: ['./duesy-widget.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class DuesyWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('fab') fab?: ElementRef<HTMLButtonElement>;

  state!: DuesyState;
  helpItems: MenuItem[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(private store: Store, public duesy: DuesyStateService) {}

  ngOnInit(): void {
    this.helpItems = buildHelpItems(() => this.open());

    combineLatest([this.store.select(ApplicationState.isAuthenticated), this.store.select(ApplicationState.getCurrentOrganization)])
      .pipe(
        map(([authenticated, org]) => (authenticated && org && org.id ? String(org.id) : null)),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((orgId) => this.duesy.activate(orgId));

    this.duesy.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => (this.state = state));
  }

  toggle(): void {
    if (this.state.panel === 'open') {
      this.minimize();
    } else {
      this.open();
    }
  }

  open(): void {
    this.duesy.setPanel('open');
  }

  minimize(): void {
    this.duesy.setPanel('minimized');
    this.focusFab();
  }

  close(): void {
    this.duesy.setPanel('closed');
    this.focusFab();
  }

  private focusFab(): void {
    // the FAB is re-rendered after the panel hides on small screens
    setTimeout(() => this.fab?.nativeElement.focus());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

export function buildHelpItems(askDuesy: () => void): MenuItem[] {
  const links = environment.duesy.helpLinks;
  const external = (label: string, icon: string, url: string): MenuItem | null =>
    url ? { label, icon, url, target: '_blank' } : null;
  const items: (MenuItem | null)[] = [
    { label: 'Ask Duesy', icon: 'pi pi-comments', command: () => askDuesy() },
    { separator: true },
    external('Help Center', 'pi pi-book', links.helpCenter),
    external('Training Videos', 'pi pi-video', links.trainingVideos),
    external('Release Notes', 'pi pi-file', links.releaseNotes),
    { separator: true },
    external('Contact Support', 'pi pi-envelope', links.contactSupport),
  ];
  // Drop links that aren't configured, and separators left dangling by that.
  const present = items.filter((item): item is MenuItem => item !== null);
  return present.filter((item, i) => !item.separator || (i > 0 && i < present.length - 1 && !present[i + 1].separator));
}
